import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Search,
  Plus,
  MoreHorizontal,
  Mail,
  UserCheck,
  UserX,
  Shield,
  Loader2,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const roleNames = {
  tenant_owner: 'Kurum Sahibi',
  center_director: 'Merkez Müdürü',
  reading_coach: 'Okuma Koçu',
  teacher: 'Öğretmen',
  intervention_specialist: 'Müdahale Uzmanı',
  counselor: 'Rehber Öğretmen',
  parent: 'Veli',
  student: 'Öğrenci',
};

const roleColors = {
  tenant_owner: 'bg-purple-100 text-purple-700',
  center_director: 'bg-blue-100 text-blue-700',
  reading_coach: 'bg-green-100 text-green-700',
  teacher: 'bg-yellow-100 text-yellow-700',
  intervention_specialist: 'bg-orange-100 text-orange-700',
  counselor: 'bg-pink-100 text-pink-700',
  parent: 'bg-slate-100 text-slate-700',
  student: 'bg-cyan-100 text-cyan-700',
};

export const UsersPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'teacher',
  });

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Kullanıcılar yüklenirken hata oluştu');
    }
  };

  const fetchInvites = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/invites`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setInvites(data);
      }
    } catch (error) {
      console.error('Failed to fetch invites:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchUsers(), fetchInvites()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/invites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(inviteForm),
      });

      if (response.ok) {
        toast.success('Davet gönderildi');
        setIsInviteDialogOpen(false);
        setInviteForm({ email: '', role: 'teacher' });
        fetchInvites();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Davet gönderilemedi');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleDeactivate = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success('Kullanıcı devre dışı bırakıldı');
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'İşlem başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleReactivate = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/users/${userId}/reactivate`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success('Kullanıcı yeniden aktifleştirildi');
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'İşlem başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/invites/${inviteId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success('Davet iptal edildi');
        fetchInvites();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'İşlem başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         u.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && u.is_active) ||
                         (statusFilter === 'inactive' && !u.is_active);
    return matchesSearch && matchesRole && matchesStatus;
  });

  const pendingInvites = invites.filter(i => i.status === 'pending');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-primary-900">
            Kullanıcı Yönetimi
          </h1>
          <p className="text-muted-foreground">
            Kurum kullanıcılarını yönetin ve davet gönderin
          </p>
        </div>
        <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="invite-user-btn">
              <Plus className="mr-2 h-4 w-4" />
              Kullanıcı Davet Et
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Yeni Kullanıcı Davet Et</DialogTitle>
              <DialogDescription>
                Kullanıcıya davet e-postası gönderilecek ve Google hesabıyla giriş yapabilecek.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleInvite}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="email">E-posta Adresi</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="ornek@okul.edu.tr"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    required
                    data-testid="invite-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Rol</Label>
                  <Select
                    value={inviteForm.role}
                    onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}
                  >
                    <SelectTrigger data-testid="invite-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {user?.role === 'tenant_owner' && (
                        <SelectItem value="center_director">Merkez Müdürü</SelectItem>
                      )}
                      <SelectItem value="reading_coach">Okuma Koçu</SelectItem>
                      <SelectItem value="teacher">Öğretmen</SelectItem>
                      <SelectItem value="intervention_specialist">Müdahale Uzmanı</SelectItem>
                      <SelectItem value="counselor">Rehber Öğretmen</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsInviteDialogOpen(false)}>
                  İptal
                </Button>
                <Button type="submit" disabled={inviteLoading} data-testid="send-invite-btn">
                  {inviteLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Davet Gönder
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pending Invites */}
      {pendingInvites.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Mail className="h-5 w-5 text-secondary" />
              Bekleyen Davetler ({pendingInvites.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingInvites.map((invite) => (
                <div key={invite.invite_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-sm">{invite.email}</p>
                    <p className="text-xs text-muted-foreground">
                      {roleNames[invite.role]} • Gönderildi: {new Date(invite.created_at).toLocaleDateString('tr-TR')}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCancelInvite(invite.invite_id)}
                  >
                    İptal Et
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="İsim veya e-posta ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="user-search-input"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="role-filter">
                <SelectValue placeholder="Rol Filtrele" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Roller</SelectItem>
                {Object.entries(roleNames).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
                <SelectValue placeholder="Durum Filtrele" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Durumlar</SelectItem>
                <SelectItem value="active">Aktif</SelectItem>
                <SelectItem value="inactive">Devre Dışı</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kullanıcı</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead>Son Giriş</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((u) => (
                <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{u.name}</p>
                      <p className="text-sm text-muted-foreground">{u.email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={roleColors[u.role]}>
                      {roleNames[u.role]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {u.is_active ? (
                      <Badge variant="outline" className="bg-green-100 text-green-700">
                        <UserCheck className="mr-1 h-3 w-3" />
                        Aktif
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-slate-100 text-slate-700">
                        <UserX className="mr-1 h-3 w-3" />
                        Devre Dışı
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {u.last_login 
                      ? new Date(u.last_login).toLocaleDateString('tr-TR')
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {u.user_id !== user?.user_id && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem>
                            <Shield className="mr-2 h-4 w-4" />
                            Rol Değiştir
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {u.is_active ? (
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => handleDeactivate(u.user_id)}
                            >
                              <UserX className="mr-2 h-4 w-4" />
                              Devre Dışı Bırak
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => handleReactivate(u.user_id)}
                            >
                              <UserCheck className="mr-2 h-4 w-4" />
                              Aktifleştir
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {filteredUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    Kullanıcı bulunamadı
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default UsersPage;
